import { endOfDay, format, isSameDay, parseISO, startOfDay } from 'date-fns';
import { enUS, fr } from 'date-fns/locale';
import jsPDF, { jsPDFOptions } from 'jspdf';
import { GRID_GAP, GridItem, paginate, pitchOf, toPixels } from 'src/app/classes/widget/dashboard-grid';

// The dashboard as displayed: each widget's cell and image, the grid size.
export interface PdfGrid {
    items: { cell: GridItem; canvas: HTMLCanvasElement }[];
    width: number;
    columns: number;
    rowHeight: number;
}

const locales: Record<string, any> = {
    "fr-FR": {
        "title": "Bulletin Hebdomadaire Chantier UNIFIELD",
        "dailyTitle": "Bulletin Journalier Chantier UNIFIELD",
        "customTitle": "Rapport généré sur app.unifield.io",
        "from": "De",
        "week": "Semaine",
        "timeFormat": "dd/MM/yyyy",
        "hourFormat": "HH:mm",
        "footer": "'Rapport généré sur app.unifield.io le' dd-MM-yyyy",
        "to": "à",
        "duration": "Durée",
        "createdDates": "Dates:",
        "createdDate": "Date: ",
        "no_image": "Aucune image à afficher",
        "list": "Liste de rapport",
        "deleteReport": "Suppression de rapport",
        "confirmDelete": "Souhaitez-vous vraiment supprimer ce rapport?",
        "no_data": "Aucune donnée à rapporter sur cette période",
        "locale": fr
    },
    "en-US": {
        "title": "Weekly Report UNIFIELD",
        "dailyTitle": "Daily Report UNIFIELD",
        "customTitle": "app.unifield.io report",
        "from": "From",
        "week": "Week",
        "timeFormat": "MM/dd/yyyy",
        "hourFormat": "hh:mm a",
        "footer": "'Report generated via app.unifield.io on' MM-dd-yyyy",
        "to": "to",
        "duration": "Duration",
        "createdDates": "Dates: ",
        "createdDate": "Date: ",
        "no_image": "No image to draw",
        "list": "Report list",
        "deleteReport": "Deleting report",
        "confirmDelete": "Do you really want to delete this report?",
        "no_data": "No data to report for this period",
        "locale": enUS
    }
};

function truncateText(text: string, maxLength: number): string {
    return text.length > maxLength ? text.substring(0, maxLength - 3).trim() + "..." : text;
}

function localeOf(lang: string): Record<string, any> {
    return locales[lang] ?? (lang?.startsWith('fr') ? locales['fr-FR'] : locales['en-US']);
}

function isDayStart(date: Date): boolean {
    return date.getHours() === 0 && date.getMinutes() === 0;
}

function isDayEnd(date: Date): boolean {
    return (date.getHours() === 23 && date.getMinutes() === 59) || isDayStart(date);
}

function formatPeriod(startDate: string, endDate: string, lang: string): string {
    const l = localeOf(lang);
    const start = parseISO(startDate);
    const end = parseISO(endDate);
    const day = (d: Date) => format(d, l['timeFormat'], { locale: l['locale'] });
    const hour = (d: Date) => format(d, l['hourFormat'], { locale: l['locale'] });

    if (isSameDay(start, end)) {
        const fullDay = isDayStart(start) && isDayEnd(end);
        return fullDay ? day(start) : `${day(start)} ${hour(start)} - ${hour(end)}`;
    }

    const withHour = (d: Date, boundary: boolean) => boundary ? day(d) : `${day(d)} ${hour(d)}`;
    return `${withHour(start, isDayStart(start))} - ${withHour(end, isDayEnd(end))}`;
}

export class PDFExporter {
    defaultOptions: jsPDFOptions = {
        orientation: 'l',
        unit: 'px',
        format: [1357, 1920]
    };
    width: number;
    height: number;
    pdf: jsPDF;
    headerHeight: number = 140;
    extMargin: number = 50;
    title: string = "New PDF Export";
    lang: string = "en-US";
    currentPage: number = 0;
    textColor: string = "#222222";
    subTitleColor: string = "#ff5722";
    infoColor: string = "#5cb04d";
    projectName: string = "";
    startDate: string = "";
    endDate: string = "";

    constructor(pdfOptions: Partial<typeof this.defaultOptions> = {}, options: { [key: string]: any; grid?: PdfGrid; } = {}) {
        this.pdf = new jsPDF(Object.keys(pdfOptions).length ? pdfOptions : this.defaultOptions);
        this.width = this.pdf.internal.pageSize.getWidth();
        this.height = this.pdf.internal.pageSize.getHeight();

        Object.keys(options).forEach(o => {
            if ((this as any)[o] !== undefined) { (this as any)[o] = options[o]; }
        });

        this.addPage(undefined, false);

        if (options.grid)
            this.build(options.grid);
    }

    // Widgets at their grid cell, scaled to the page width, a page per band of rows.
    build({ items, width, columns, rowHeight }: PdfGrid) {
        const scale = (this.width - 2 * this.extMargin) / width;
        const pitch = pitchOf(width, columns, rowHeight);
        const rows = Math.floor((this.height - this.headerHeight - this.extMargin + GRID_GAP) / (pitch.y * scale));
        const canvases = new Map(items.map(item => [item.cell.id, item.canvas]));

        paginate(items.map(item => item.cell), rows).forEach((page, index) => {
            if (index > 0) this.addPage();
            const s = scale * page.scale;

            for (const cell of page.items) {
                const size = toPixels(cell, width, columns, rowHeight);
                this.pdf.addImage(canvases.get(cell.id)!.toDataURL('image/png'), 'PNG',
                    this.extMargin + cell.x * pitch.x * s, this.headerHeight + (cell.y - page.top) * pitch.y * s, size.width * s, size.height * s);
            }
        });
    }

    addPage(pageTitle = "", add = true) {
        try {
            const lang = this.lang;
            if (!this.title) this.title = pageTitle;
            this.currentPage++;
            if (add) this.pdf.addPage();
            this.addBg();

            // Project Name
            this.pdf.setTextColor(this.textColor);
            const projectFontSize = 42;
            this.pdf.setFontSize(projectFontSize);
            let projectName = truncateText(this.projectName, 53);
            this.pdf.text(projectName, this.extMargin, projectFontSize);

            // Title
            this.pdf.setTextColor(this.subTitleColor);
            const tabFontSize = 48;
            this.pdf.setFontSize(tabFontSize);
            this.pdf.text(truncateText(this.title, 48), this.extMargin, projectFontSize + tabFontSize);

            // Info (date)
            const infoFontSize = 36;
            this.pdf.setFontSize(infoFontSize);

            const now = new Date();
            const period = this.startDate
                ? formatPeriod(this.startDate, this.endDate || this.startDate, lang)
                : formatPeriod(startOfDay(now).toISOString(), endOfDay(now).toISOString(), lang);
            this.pdf.setTextColor("#666666");
            this.pdf.text(period, this.width - this.extMargin, infoFontSize + this.extMargin * .5, { align: "right" });

            // Custom Title
            const l = localeOf(lang);
            this.pdf.setTextColor(this.infoColor);
            this.pdf.setFontSize(36);
            this.pdf.text(format(now, l['footer'], { locale: l['locale'] }), this.extMargin, this.height - this.extMargin * .6);

            // Page Number
            this.pdf.setTextColor(this.textColor);
            this.pdf.setFontSize(36);
            this.pdf.text(this.currentPage.toString(), this.width * .5, this.height - this.extMargin, { align: "center" });
        } catch (e) {
            console.error(e);
        }
    }

    async addLogo() {
        const img = new Image();
        img.src = "/img/CAD42_logo.jpg";

        await new Promise(resolve => {
            img.onload = resolve;
        });

        const width = this.width * 0.15;
        const height = width * img.height / img.width;
        this.pdf.addImage(img, "JPEG", this.width / 2 - width / 2, this.extMargin, width, height);
    }

    addBg() {
        this.pdf.addImage("/img/export-bg.jpg", "JPEG", 0, 0, this.width, this.height);
    }

    save(title: string | null = null) {
        if (title) {
            this.title = title;
        }
        this.pdf.save(this.title + '.pdf');
    }
}